import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

interface IUserContext {
    isLoading: boolean
    user: User

    saveUser: (user: User) => void
}

export interface Customer {
    customerId: string
    customerName: string
}

export let userSchema = yup.object().shape({
    userId: yup.string(),
    firstName: yup.string().default('').required().label("First name"),
    lastName: yup.string().default('').required().label("Last name"),
    email: yup.string().default('').required().label("Email"),
    canCreateTicket: yup.boolean().default(false).label("Can Create Ticket"),
    canBeReporter: yup.boolean().default(false).label("Can Be Reporter"),
    canDeleteTicket: yup.boolean().default(false).label("Can Delete Ticket"),
    ticketManagement: yup.string()
        .oneOf(['', 'CanView', 'CanWorkOnAllTickets', 'CanWorkOnMyTickets', 'CanWorkOnTicketForMyGroup', "CannotWorkOnTickets"])
        .default('CanView'),
    schedulingManagement: yup.string()
        .oneOf(['', 'None', 'ReadOnlyScheduling', 'EditScheduling'])
        .default('None'),
    groups: yup.array(yup.string()).default([])
        .when("ticketManagement", {
            is: 'CanWorkOnTicketForMyGroup',
            then: yup.array(yup.string()).default([]).min(1, "At least one group must be specified"),
            otherwise: yup.array(yup.string()).default([])
        }).label("Groups"),
    canBeAssignedToTicket: yup.boolean().default(false).label("Can Be Assigned To Ticket"),
    canGoToCustomer: yup.boolean().default(false).label("Can Go To Customer"),
    canManageCustomerUsers: yup.boolean().default(false).label("Can Manage Customer Users"),
    canManageSimecUsers: yup.boolean().default(false).label("Can Manage Simec Users"),
    canViewSummary: yup.boolean().default(false).label("Can View Summary"),
    canManageTicketCategories: yup.boolean().default(false).label("Can Manage Ticket Categories"),
    canManageToolTypologies: yup.boolean().default(false).label("Can Manage Tool Typologies"),
    isSubscribedToToolMaintenanceNotifications: yup.boolean().default(false).label("Is Subscribed To Tool Maintenance Notifications"),
    canLoginAs: yup.boolean().default(false).label("Can Login As"),
    canManageCanGoToCustomerSimecUsers: yup.boolean().default(false).label("Can Manage Can Go To Customer Simec Users"),
    canManageTicketInbox: yup.boolean().default(false).label("Can manage ticket inbox"),
    canManageTools: yup.boolean().default(false).label("Can manage tools"),
    canViewDashboard: yup.boolean().default(false).label("Can view dashboard"),
    isSubscribedToTicketDoneNotifications: yup.boolean().default(false).label("Is subscribed to ticket done notifications"),
    canMoveMessages: yup.string().oneOf(['None', 'Mine', 'All']).default('None').label("Can move messages")
});

type User = yup.InferType<typeof userSchema>;

export let userContext = React.createContext({} as IUserContext);

let { Provider } = userContext;

let UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let location = useLocation();
    let history = useHistory();
    let { enqueueSnackbar } = useSnackbar();

    let isNew = location.pathname.includes("/addnew");

    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [user, setUser] = useState<User>(userSchema.getDefault());


    useEffect(() => {
        if (isNew === false)
            getUser();

    }, [id, isNew])

    let getUser = async () => {
        try {
            let res = await axios.get('/api/User/GetUser', { params: { userId: id } });

            let { item } = res.data;
            setUser({
                ...item,
                adminArea: item.adminArea ?? [],
                groups: item.groups ?? [],
                enabledLocales: item.enabledLocales ?? [],
                ticketManagement: item.ticketManagement ?? ''
            });
            setLoading(false);

        } catch (err) { }
        finally {
        }
    }

    let saveUser = async (value: User) => {
        try {
            await axios.post('/api/User/SaveUser', value);
            enqueueSnackbar("User saved", { variant: "success" });
            history.push(`/users`);

        } catch (err) {}
    }


    return (
        <Provider
            value={{
                isLoading,
                user,

                saveUser
            }}
        >
            {children}
        </Provider>
    )
}

export default UserProvider;